'use client';

import type {
  CategoriesEntity,
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Topbar } from '@eliadmin/core/src/components/header/topbar';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SecondaryHeader } from '@eliadmin/core/src/components/header/secondary-header';
import { MainHeader } from '@eliadmin/core/src/components/header/main-header';

/** Trims a trailing slash so `/cart` and `/cart/` (and `''` vs `/`) compare equal. */
function normalizePath(path: string) {
  const trimmed = (path || '/').replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

export function HeaderClient({
  generalLayout,
  topbarMenu,
  categories,
  mobileSidebarCategories,
}: {
  generalLayout: GeneralLayout['data'];
  topbarMenu: Menu;
  categories: CategoriesEntity[];
  mobileSidebarCategories: CategoriesEntity[];
}) {
  const pathname = usePathname();
  const supportedLocales = useLocaleStore(s => s.supportedLocales);
  const [scrolled, setScrolled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () =>
      document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, []);

  /**
   * `ROUTES.*` returns a locale-less slug while `usePathname()` keeps the `/sk` prefix, so a raw
   * equality check was false on every non-default locale — the homepage on /sk rendered the solid
   * header instead of the transparent one over the hero.
   */
  const routePath = useMemo(() => {
    const codes = (supportedLocales ?? []).map(item => item.locale).filter(Boolean);
    const [, first] = pathname.split('/');
    return first && codes.includes(first) ? pathname.slice(first.length + 1) || '/' : pathname;
  }, [pathname, supportedLocales]);

  const matchesRoute = (route: string) => normalizePath(routePath) === normalizePath(route);

  const isHomepage = matchesRoute(ROUTES.HOME());
  const isProfile = matchesRoute(ROUTES.PROFILE());
  const isCart = matchesRoute(ROUTES.CART());

  const whiteText = isHomepage && !scrolled;

  return (
    <div ref={containerRef} className="sticky top-0 z-40">
      <Topbar generalLayout={generalLayout} menu={topbarMenu} />
      {isProfile || isCart ? (
        <SecondaryHeader isCart={isCart} isProfile={isProfile} />
      ) : (
        <MainHeader
          categories={categories}
          mobileSidebarCategories={mobileSidebarCategories}
          topbarMenu={topbarMenu}
          generalLayout={generalLayout}
          isHomepage={isHomepage}
          whiteText={whiteText}
        />
      )}
    </div>
  );
}
